import { useEffect, useState, useCallback } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { UsersRound, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import logger from "@/lib/logger";

const STATUSES = ["not_started", "in_progress", "complete", "not_applicable"];

export default function IEC62366() {
  const { loading: authLoading } = useAuth();
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState("");
  const [data, setData] = useState({ items: [], counts: {}, readiness_pct: 0, by_phase: {} });
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ status: "not_started", evidence_link: "", notes: "" });

  useEffect(() => {
    if (authLoading) return;
    api.get("/devices").then(({ data }) => {
      setProjects(data); if (data[0]) setPid(data[0].project_id);
    }).catch((e) => logger.warn("Projects:", e?.response?.status));
  }, [authLoading]);

  const load = useCallback(() => {
    if (!pid) return;
    api.get(`/iec62366/checklist/${pid}`).then(({ data }) => setData(data))
      .catch((e) => logger.warn("Checklist:", e?.response?.status));
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  const openEditor = (item) => {
    setEdit(item);
    setForm({ status: item.status || "not_started", evidence_link: item.evidence_link || "", notes: item.notes || "" });
  };
  const save = async () => {
    try {
      await api.post("/iec62366/evidence", { project_id: pid, clause: edit.clause, ...form });
      toast.success("Saved."); setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
  };

  const phases = Object.entries(data.by_phase || {});

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-7xl">
        <div className="mb-6">
          <div className="vq-overline mb-2 flex items-center gap-2"><UsersRound className="w-3 h-3" /> IEC 62366-1</div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900">
            Usability Engineering
          </h1>
          <p className="text-slate-600 mt-2 max-w-3xl">
            IEC 62366-1 evidence checklist across use-specification, formative &amp; summative evaluation, and post-market
            usability trending. Draft scaffolds only — a qualified usability engineer must sign off each artefact.
          </p>
        </div>

        <div className="mb-4 max-w-md">
          <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Device project</Label>
          <Select value={pid} onValueChange={setPid}>
            <SelectTrigger data-testid="iec62366-project-select"><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>
              {projects.map((p) => <SelectItem key={p.project_id} value={p.project_id}>{p.device_name || p.project_id}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {pid && (
          <>
            <div className="vq-card p-4 mb-4 flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center">
                <div className="font-bold text-2xl text-teal-700" data-testid="iec62366-readiness-pct">{data.readiness_pct}%</div>
              </div>
              <div className="flex-1">
                <div className="font-display font-bold text-slate-900">Usability engineering readiness</div>
                <div className="text-xs text-slate-500 mt-1">
                  {data.counts?.complete || 0} complete · {data.counts?.in_progress || 0} in progress · {data.counts?.not_started || 0} not started · {data.counts?.not_applicable || 0} N/A
                  <span className="ml-2">(of {data.total} items)</span>
                </div>
              </div>
            </div>

            {phases.length > 0 && (
              <div className="vq-card p-4 mb-4">
                <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-3">Usability phases</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="iec62366-phases">
                  {phases.map(([ph, c]) => (
                    <div key={ph} className="border border-slate-200 rounded-md p-3">
                      <div className="text-sm font-semibold text-slate-900">{ph}</div>
                      <div className="text-xs text-slate-500 mt-1">{c.complete} / {c.total} complete</div>
                      <div className="mt-1 h-1.5 bg-slate-100 rounded">
                        <div className="h-full bg-teal-600 rounded" style={{ width: `${(c.complete / Math.max(c.total, 1)) * 100}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="vq-card overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2 w-24">Clause</th>
                    <th className="text-left px-3 py-2 w-40">Phase</th>
                    <th className="text-left px-3 py-2">Item</th>
                    <th className="text-left px-3 py-2 w-40">Status</th>
                    <th className="text-right px-3 py-2 w-24"></th>
                  </tr>
                </thead>
                <tbody data-testid="iec62366-tbody">
                  {data.items.map((i) => (
                    <tr key={i.clause} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-mono text-xs">{i.clause}</td>
                      <td className="px-3 py-2 text-xs text-slate-600">{i.phase}</td>
                      <td className="px-3 py-2">{i.title}</td>
                      <td className="px-3 py-2 text-xs">
                        <span className={
                          i.status === "complete" ? "text-emerald-700 font-semibold" :
                          i.status === "in_progress" ? "text-amber-700 font-semibold" :
                          i.status === "not_applicable" ? "text-slate-500" : "text-slate-500"
                        }>{i.status.replace("_", " ")}</span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button size="sm" variant="ghost" data-testid={`edit-iec62366-${i.clause}`} onClick={() => openEditor(i)}>Edit</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="vq-card p-4 border-l-4 border-l-amber-500 bg-amber-50 flex items-start gap-3 mt-4">
              <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-amber-900">
                Summative evaluation (5.10) typically requires ≥ 15 participants per distinct user group in a
                representative environment. Consult a qualified usability engineer.
              </div>
            </div>
          </>
        )}

        <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{edit?.clause} — {edit?.title}</DialogTitle>
              <DialogDescription>Update evidence and status for this IEC 62366-1 clause.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="iec62366-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Evidence link</Label>
                <Input data-testid="iec62366-evidence" value={form.evidence_link} onChange={(e) => setForm({ ...form, evidence_link: e.target.value })} />
              </div>
              <div>
                <Label>Reviewer notes</Label>
                <Textarea data-testid="iec62366-notes" rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setEdit(null)}>Cancel</Button>
              <Button data-testid="iec62366-save" onClick={save}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
